import type { ReactNode } from "react";

import { InlineText } from "../inline-text";

export { InlineText as ConceptText };

interface ConceptCodeBlockProps {
  caption: string;
  lines: readonly string[];
}

export function ConceptCodeBlock({ caption, lines }: ConceptCodeBlockProps) {
  return (
    <figure className="bg-card overflow-hidden rounded-md border shadow-sm">
      <figcaption className="bg-muted/30 border-b px-4 py-3 text-xs font-medium">
        {caption}
      </figcaption>
      <div className="bg-background overflow-x-auto py-2 text-[13px] leading-6">
        {lines.map((line, index) => (
          <code
            key={`${index}-${line}`}
            className="grid min-w-max grid-cols-[3.25rem_minmax(0,1fr)] font-mono"
            style={{ tabSize: 4 }}
          >
            <span className="bg-muted/40 text-muted-foreground border-r px-3 text-right select-none">
              {index + 1}
            </span>
            <span className="px-4 whitespace-pre">{line || "\u00a0"}</span>
          </code>
        ))}
      </div>
    </figure>
  );
}

interface ConceptDefinitionProps {
  children: ReactNode;
  id?: string;
  term: string;
}

export function ConceptDefinition({
  children,
  id,
  term,
}: ConceptDefinitionProps) {
  return (
    <div className="grid scroll-mt-20 gap-2 py-4 first:pt-0 last:pb-0" id={id}>
      <dt className="font-semibold">{term}</dt>
      <dd className="text-muted-foreground leading-7">{children}</dd>
    </div>
  );
}

interface ConceptStepProps {
  children: ReactNode;
  number: string;
}

export function ConceptStep({ children, number }: ConceptStepProps) {
  return (
    <li className="grid grid-cols-[2rem_minmax(0,1fr)] items-start gap-3">
      <span className="bg-secondary text-secondary-foreground ring-border/70 flex size-8 items-center justify-center rounded-md font-mono text-xs shadow-xs ring-1">
        {number}
      </span>
      <p className="text-muted-foreground pt-0.5 leading-7">{children}</p>
    </li>
  );
}
